import { QUERY } from '../../../gql-operations';
import { DateField } from '../../../constants/dateField';

async function loadMoreFilteredFolders(
    text,
    folders,
    fetchMore,
    {
        loadingMore,
        cursor: pageCursor,
        setLoadingOlder,
        setLoadMoreCheck
    },
    sortBy = DateField.CREATED_AT
) {
    // Skip if already loading
    if (loadingMore) {
        return;
    }

    try {
        setLoadingOlder(true);
        // Prefer the continuation token issued by the server, including tie breakers.
        const cursor = pageCursor !== undefined ? pageCursor : folders[folders.length - 1]?.[sortBy];
        if (!cursor) {
            setLoadMoreCheck(false);
            return;
        }

        const result = await fetchMore({
            query: QUERY.FIND_FOLDERS,
            variables: {
                data: {
                    text: text?.trim() || '',
                    cursor,
                    sortBy
                }
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult?.findFolders?.folders) {
                    setLoadMoreCheck(false);
                    return previousResult;
                }

                const previousFolders = previousResult?.findFolders?.folders;
                if (!previousFolders) {
                    // The modal may have closed or changed its query variables
                    // while this page was in flight, leaving no compatible
                    // cached result to merge into.
                    setLoadMoreCheck(false);
                    return fetchMoreResult;
                }
                const newFolders = fetchMoreResult.findFolders.folders;

                // Short or overlapping pages may still have more results.
                const nextCursor = fetchMoreResult.findFolders.cursor !== undefined
                    ? fetchMoreResult.findFolders.cursor
                    : newFolders[newFolders.length - 1]?.[sortBy];
                setLoadMoreCheck(newFolders.length > 0 && Boolean(nextCursor) && nextCursor !== cursor);

                // Preserve server order and deduplicate within and across pages.
                const existingIds = new Set(previousFolders.map(folder => folder.id));
                const uniqueNewFolders = newFolders.filter(folder => {
                    if (existingIds.has(folder.id)) return false;
                    existingIds.add(folder.id);
                    return true;
                });

                return {
                    findFolders: {
                        ...previousResult.findFolders,
                        ...fetchMoreResult.findFolders,
                        folders: [...previousFolders, ...uniqueNewFolders],
                        cursor: fetchMoreResult.findFolders.cursor,
                        __typename: previousResult.findFolders.__typename
                    }
                };
            }
        });

        return result;
    } catch (error) {
        console.error('Error loading more folders:', error);
    } finally {
        setLoadingOlder(false);
    }
}

export default loadMoreFilteredFolders;
